import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import { AuthService } from '../../core/auth/auth.service';
import { CertificateDetailResponse, CertificateStatus } from '../../core/models/portal.models';

@Component({
  selector: 'app-certificate-detail-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './certificate-detail-page.component.html'
})
export class CertificateDetailPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(AuthService);

  protected readonly detail = signal<CertificateDetailResponse | null>(null);
  protected readonly notice = signal<string | null>(null);
  protected readonly downloading = signal(false);
  protected readonly management = computed(() => this.auth.hasAnyRole(['SUPER_ADMIN', 'ORGANIZATION_ADMIN', 'COMPANY_ADMIN', 'QUALITY_MANAGER']));

  private certificateId = 0;

  ngOnInit(): void {
    this.certificateId = Number(this.route.snapshot.paramMap.get('id'));
    this.api.certificate(this.certificateId).subscribe((detail) => this.detail.set(detail));
  }

  protected downloadPdf(item: CertificateDetailResponse): void {
    if (this.downloading()) {
      return;
    }

    this.downloading.set(true);
    this.api.downloadCertificatePdf(item.id).subscribe({
      next: (blob) => {
        this.saveBlob(blob, item.pdfFileName);
        this.notice.set(`PDF ${item.pdfFileName} wurde heruntergeladen.`);
        this.downloading.set(false);
      },
      error: () => {
        this.notice.set('Das PDF konnte nicht geladen werden.');
        this.downloading.set(false);
      }
    });
  }

  protected formatDate(value: string): string {
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short' }).format(new Date(value));
  }

  protected formatDateTime(value: string): string {
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
  }

  protected formatScore(value: number | null): string {
    if (value === null) {
      return '-';
    }

    return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value)} %`;
  }

  protected valueOrDash(value: string | null): string {
    return value?.trim() ? value : '-';
  }

  protected statusLabel(status: CertificateStatus): string {
    const labels: Record<CertificateStatus, string> = {
      ISSUED: 'Ausgestellt',
      VALID: 'Gültig',
      EXPIRED: 'Abgelaufen',
      REVOKED: 'Widerrufen'
    };

    return labels[status] ?? status;
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.rel = 'noopener';
    anchor.click();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
  }
}
